import React, { useState, useEffect, useCallback } from 'react';
import { api, endpoints } from '../../config/api';
import { Card, Button, Alert, Spinner, Badge } from '../ui';
import { useT, apiErrorMessage } from '../../i18n';
import { setupMessages, failureKey } from './setupMessages';
import './GraphConnectionCard.css';

const messages = {
  ...setupMessages,
  confirmDisconnect: {
    tr: 'Google Workspace bağlantısını kaldırmak istediğinize emin misiniz? İmza dağıtımı bu bağlantı olmadan çalışmaz.',
    en: 'Are you sure you want to remove the Google Workspace connection? Signature distribution doesn’t work without it.',
  },
  yourAccount: { tr: 'Google Workspace hesabınız', en: 'your Google Workspace account' },
  connectedDesc: {
    tr: ({ account, deployed }) => <><strong>{account}</strong> ile bağlantı kuruldu. Personel ve grup bilgileriniz senkronize ediliyor.{deployed ? ' İmzalar Gmail kullanıcılarınıza otomatik olarak dağıtılıyor.' : ' İmzaların Gmail\'de otomatik görünmesi için aşağıdaki Add-on dağıtım adımını da tamamlamanız gerekir.'}</>,
    en: ({ account, deployed }) => <>Connected to <strong>{account}</strong>. Your people and group data is being synchronized.{deployed ? ' Signatures are being distributed to your Gmail users automatically.' : ' To have signatures appear in Gmail automatically, also complete the add-on deployment step below.'}</>,
  },
};

const DATE_OPTS = { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' };

// Mirrors GraphConnectionCard.jsx — same layout/behavior, Google Workspace
// as the directory provider instead of Microsoft 365. Reuses
// GraphConnectionCard.css's generic (non-Microsoft-specific) class names.
export const GoogleConnectionCard = ({ resultParam, disabledReason }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState('');
  const { t, formatDate } = useT(messages);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get(endpoints.organizations.googleStatus);
      setStatus(res);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    if (!resultParam) return;
    if (resultParam.connected === '1') {
      setError('');
    } else if (resultParam.connected === '0') {
      setError(t(failureKey(resultParam.reason, 'google')));
    }
  }, [resultParam, t]);

  const handleConnect = async () => {
    setConnecting(true);
    setError('');
    try {
      const res = await api.get(endpoints.organizations.googleConsentUrl);
      window.location.href = res.consent_url;
    } catch (err) {
      setError(apiErrorMessage(err, messages.startFailed));
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(t('confirmDisconnect'))) return;
    setDisconnecting(true);
    try {
      await api.post(endpoints.organizations.googleDisconnect);
      await fetchStatus();
    } catch {
      setError(t('disconnectFailed'));
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <Card className="graph-connection-card">
        <Spinner size="sm" />
      </Card>
    );
  }

  const connected = status?.connected;

  if (!connected && disabledReason) {
    return (
      <Card className="graph-connection-card not-connected">
        <div className="graph-connection-header">
          <div className="graph-connection-title">
            <span className="graph-connection-icon">
              <GoogleGIcon />
            </span>
            <h3>{t('googleTitle')}</h3>
          </div>
          <Badge variant="warning">{t('notConnected')}</Badge>
        </div>
        <p className="graph-connection-desc">{disabledReason}</p>
      </Card>
    );
  }

  return (
    <Card className={`graph-connection-card ${connected ? 'connected' : 'not-connected'}`}>
      <div className="graph-connection-header">
        <div className="graph-connection-title">
          <span className="graph-connection-icon">
            <GoogleGIcon />
          </span>
          <h3>{t('googleTitle')}</h3>
        </div>
        {connected ? (
          <Badge variant="success">{t('connected')}</Badge>
        ) : (
          <Badge variant="warning">{t('notConnected')}</Badge>
        )}
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {connected ? (
        <>
          <p className="graph-connection-desc">
            {t('connectedDesc', { account: status.admin_email || t('yourAccount'), deployed: status.addon_deployed })}
          </p>
          {status.connected_at && (
            <p className="graph-connection-meta">{t('connectedAt', { date: formatDate(status.connected_at, DATE_OPTS) })}</p>
          )}
          <Button variant="secondary" onClick={handleDisconnect} disabled={disconnecting}>
            {disconnecting ? t('removing') : t('disconnect')}
          </Button>
        </>
      ) : (
        <>
          <p className="graph-connection-desc">{t('googleIntro')}</p>
          <Button onClick={handleConnect} disabled={connecting}>
            {connecting ? t('redirecting') : t('connectGoogle')}
          </Button>
        </>
      )}
    </Card>
  );
};

const GoogleGIcon = () => (
  <svg width="22" height="22" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
    <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z" />
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
  </svg>
);

export default GoogleConnectionCard;
